import { useDispatch, useSelector } from '@redux';
import RestShow from 'containers/rest/Show';
import React, { useCallback, useMemo, useState } from 'react';
import { DynamicObjectType } from 'types/commons';
import ShowContent from './ShowContent';
import { Modal, Tag, notification } from 'antd';
import { SELLER_ORDER_STATUS_CONST } from 'constants/constants';
import { useTranslation } from 'react-i18next';
import crudActions from '@redux/crudActions';
import RefreshOrderModal from '../components/RefreshOrderModal';
import CancelModal from './CancelModal';
import EditInvoiceModal from '../components/EditInvoiceModal';
import {
  confirmForceToCOD,
  markAsRefundExchange,
  markDeliverySuccess,
  moveToLost,
  moveToReadyToPick,
  revertToPreviousState,
} from '@redux/orders/actions';
import ReplaceModal from './ReplaceModal';
import ReplaceSKUModal from '../components/ReplaceSKUModal';
import ConfirmModal from '../components/ConfirmModal';
// import PrintLabelActions from '../components/PrintLabelActions';
import { differentLabelChannels, formatDateTime } from 'utils/formattedString';
import { useNavigate } from 'react-router-dom';
import { useGetRootPath } from 'hooks/useShowLayout';
import ConfirmPaidModal from './ConfirmPaidModal';
import MoveToPendingModal from './MoveToPendingModal';
import PrintLabelActions from '../components/PrintLabelActions';
import MarkAsSuccessModal from '../components/Modal/MarkAsSuccessModal';
import ConfirmRefundModal from '../components/Modal/ConfirmRefundModal';

const TYPE = {
  editDelivery: [
    'AWAITING_CONFIRMATION',
    'CONFIRMED',
    'READY_TO_PICK',
    'PICKING',
  ], //1
  editInvoice: ['AWAITING_CONFIRMATION'], //2
  confirmPaid: ['AWAITING_CONFIRMATION'], //3
  changePaymentToCOD: ['AWAITING_CONFIRMATION'], //4
  refreshOrder: [
    'AWAITING_CONFIRMATION',
    'CONFIRMED',
    'READY_TO_PICK',
    'PICKING',
    // 'PACKING',
    'PENDING_PROCESSING',
  ], //5
  replaceSKU: ['AWAITING_CONFIRMATION'], //6
  cancelOrder: [
    'AWAITING_CONFIRMATION',
    'CONFIRMED',
    'READY_TO_PICK',
    'PICKING',
    'PACKING',
    'PENDING_PROCESSING',
    'LOST',
    'FAIL_QC',
    'READY_TO_HANDOVER',
  ], //7
  confirmOrder: ['AWAITING_CONFIRMATION'], //8
  printPickingList: [
    // 'PICKING',
    // 'PACKING',
    // 'READY_TO_HANDOVER',
    // {
    //   title: 'DELIVERY',
    //   keyPath: 'DELIVERY',
    // },
    // 'FAILED_DELIVERY',
    // 'FAIL_QC',
    // 'RETURNING',
    // 'DELIVERING',
  ], //9
  printLabel: [
    'PACKING',
    'READY_TO_HANDOVER',
    {
      title: 'DELIVERY',
      keyPath: 'DELIVERY',
    },
    'FAILED_DELIVERY',
    'FAIL_QC',
    'RETURNING',
    'DELIVERING',
  ], //10
  markAsSuccess: ['READY_TO_HANDOVER', 'LOST', 'FAIL_QC'], //11
  moveToLost: [
    {
      title: 'DELIVERY',
      keyPath: 'DELIVERY',
    },
    'FAILED_DELIVERY',
    'RETURNING',
    'DELIVERING',
  ], //12
  exportGDN: [
    {
      title: 'DELIVERY',
      keyPath: 'DELIVERY',
    },
    'FAILED_DELIVERY',
    'RETURNING',
    'REJECTED',
    'FAIL_QC',
    'DELIVERY_SUCCESS',
    'LOST',
    'DELIVERING',
  ], //13
  moveToPending: [
    'AWAITING_CONFIRMATION',
    'CONFIRMED',
    'READY_TO_PICK',
    'PICKING',
    'PACKING',
  ], //14
  revertToPreviousState: ['PENDING_PROCESSING'], //15
  moveToReadyToPick: ['PENDING_PROCESSING'], //16
  confirmRefundExchange: ['CANCELLED'], //17
  markAsRefundExchange: ['CANCELLED'], //18
  markAsRefund: ['CANCELLED'], //19
};

const KEY_PATH = {
  AWAITING_CONFIRMATION: {
    title: 'Awaiting Confirmation',
    keyPath: 'CONFIRMATION',
  },
  CONFIRMED: {
    title: 'Confirmed',
    keyPath: 'CONFIRMATION',
  },
  READY_TO_PICK: {
    title: 'Ready to pick',
    keyPath: 'PROCESSING',
  },
  PICKING: {
    title: 'Picking',
    keyPath: 'PROCESSING',
  },
  PACKING: {
    title: 'Packing',
    keyPath: 'PROCESSING',
  },
  READY_TO_HANDOVER: {
    title: 'Ready to handover',
    keyPath: 'PROCESSING',
  },
  DELIVERING: {
    title: 'Delivering',
    keyPath: 'DELIVERING',
  },
  FAILED_DELIVERY: {
    title: 'Failed delivery',
    keyPath: 'DELIVERING',
  },
  RETURNING: {
    title: 'Returning',
    keyPath: 'DELIVERING',
  },
  LOST: {
    title: 'Lost',
    keyPath: 'DELIVERING',
  },
  REJECTED: {
    title: 'Rejected',
    keyPath: 'DELIVERING',
  },
  FAIL_QC: {
    title: 'Fail QC',
    keyPath: 'FAIL_QC',
  },
  PENDING_PROCESSING: {
    title: 'Pending for processing',
    keyPath: 'PENDING',
  },
};

const PENDING_TYPE = {
  AUTOMATICALLY: {
    title: 'Automatically Pending',
    path: 'AUTOMATICALLY_PENDING',
  },
  MANUALLY: {
    title: 'Manually Pending',
    path: 'MANUALLY_PENDING',
  },
  EXCHANGE: {
    title: 'Exchange Pending',
    path: 'EXCHANGE_PENDING',
  },
  TAB_TRY_BUY: {
    title: 'Pending for Keep & Try',
    path: 'PENDING_FOR_TAB_TRY_BUY',
  },
};

const JOIN = [
  'orderBatch',
  'shop',
  'orderItems',
  'transferOrders||id,toId,status,fromWarehouseId,toWarehouseId',
  'orderReturn||id,returnNumber',
  'orderShipping',
  'newOrderExchange',
  'orderExchanges||exchangeNumber,id',
];

const createOption = (condition, label, key) =>
  condition ? { label, key } : null;
const Show = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useGetRootPath();

  const data = useSelector<DynamicObjectType>(
    (state) => state.orders.currentData,
  );

  const confirmRefundModalRef = React.useRef(null);

  const [refreshVisible, setRefreshVisible] = useState(false);
  const [cancelVisible, setCancelVisible] = useState(false);
  const [editInvoiceVisible, setEditInvoiceVisible] = useState(false);
  const [currentVariantSKU, setCurrentVariantSKU] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [visiblePrintModal, setVisiblePrintModal] = useState(false);
  const [confirmPaidModal, setConfirmPaidModal] = useState(false);
  const [moveToPendingModal, setMoveToPendingModal] = useState(false);
  const [loadingMarkAsSuccess, setLoadingMarkAsSuccess] = useState(false);

  const hasActionRefund = useCallback(
    (paymentTypes) => {
      const {
        newOrderExchangeId,
        paymentType,
        paymentStatus,
        newOrderExchange,
      } = data || {};

      const isRefundStatus = ['waiting_for_refund', 'refund_failed'].includes(
        paymentStatus,
      );

      if (!newOrderExchangeId) {
        return paymentType === 'bank' && isRefundStatus;
      }

      const hasAdditionalPayAmount = newOrderExchange?.additionalPayAmount > 0;
      return (
        paymentTypes.includes(paymentType) &&
        hasAdditionalPayAmount &&
        isRefundStatus
      );
    },
    [data],
  );

  const isManuallyPending =
    data?.pendingType === 'MANUALLY' &&
    data?.statusOms === 'PENDING_PROCESSING';

  const hasRefreshOrder = useMemo(() => {
    const { newOrderExchangeId, statusOms, pendingType } = data || {};

    if (newOrderExchangeId) {
      if (statusOms === 'PENDING_PROCESSING') {
        return pendingType === 'MANUALLY' || pendingType === 'AUTOMATICALLY';
      }
      return TYPE['refreshOrder'].includes(statusOms);
    }

    return TYPE['refreshOrder'].includes(statusOms);
  }, [data?.statusOms, data?.newOrderExchangeId]);

  const hasCancelOrder = useMemo(() => {
    if (data?.newOrderExchangeId) {
      return ['LOST', 'FAIL_QC'].includes(data?.statusOms);
    } else {
      return TYPE['cancelOrder'].includes(data?.statusOms);
    }
  }, [data?.statusOms]);

  const hasRevertToPreviousState = useMemo(() => {
    return data?.newOrderExchangeId
      ? data?.statusOms === 'PENDING_PROCESSING'
        ? isManuallyPending || data?.pendingType === 'AUTOMATICALLY'
        : TYPE['revertToPreviousState'].includes(data?.statusOms)
      : TYPE['revertToPreviousState'].includes(data?.statusOms);
  }, [data?.statusOms]);

  const EXTRA_OPTIONS = useMemo(
    () =>
      [
        createOption(
          TYPE['editDelivery'].includes(data?.statusOms),
          'Edit delivery information',
          'editDelivery',
        ),
        createOption(
          TYPE['editInvoice'].includes(data?.statusOms),
          'Edit invoice information',
          'editInvoice',
        ),
        createOption(
          TYPE['confirmPaid'].includes(data?.statusOms) &&
            data?.paymentStatus !== 'paid',
          'Confirm paid',
          'confirmPaid',
        ),
        createOption(
          TYPE['changePaymentToCOD'].includes(data?.statusOms) &&
            data?.paymentType !== 'cod' &&
            data?.paymentStatus !== 'paid',
          'Change payment to COD',
          'changePaymentToCOD',
        ),
        createOption(hasRefreshOrder, 'Refresh order', 'refreshOrder'),
        createOption(
          TYPE['replaceSKU'].includes(data?.statusOms),
          'Replace SKU',
          'replaceSKU',
        ),
        createOption(hasCancelOrder, 'Cancel order', 'cancelOrder'),
        createOption(
          TYPE['markAsSuccess'].includes(data?.statusOms),
          'Mark as Success',
          'markAsSuccess',
        ),
        createOption(
          TYPE['exportGDN'].includes(data?.statusOms),
          'Export GDN',
          'exportGDN',
        ),
        createOption(
          TYPE['moveToLost'].includes(data?.statusOms),
          'Move to Lost',
          'moveToLost',
        ),
        createOption(
          TYPE['moveToPending'].includes(data?.statusOms),
          'Move to pending',
          'moveToPending',
        ),
        createOption(
          hasRevertToPreviousState,
          'Revert to Previous State',
          'revertToPreviousState',
        ),
        createOption(
          TYPE['printPickingList'].includes(data?.statusOms),
          'Print picking list',
          'printPickingList',
        ),
        createOption(
          TYPE['printLabel'].includes(data?.statusOms),
          'Print label',
          'printLabel',
        ),
        createOption(
          data?.paymentStatus === 'refund_failed' &&
            (data?.pendingType !== 'EXCHANGE' || !data?.newOrderExchangeId),
          'Confirm Refund',
          'confirmRefund',
        ),
        createOption(
          TYPE['confirmRefundExchange'].includes(data?.statusOms) &&
            data?.newOrderExchangeId &&
            hasActionRefund(['payoo', 'zalopay', 'fundiin']),
          'Confirm Refund',
          'confirmRefundExchange',
        ),
        createOption(
          TYPE['markAsRefundExchange'].includes(data?.statusOms) &&
            data?.newOrderExchangeId &&
            hasActionRefund(['payoo', 'zalopay', 'fundiin', 'bank']),
          'Mark as Refund',
          'markAsRefundExchange',
        ),
        createOption(
          TYPE['markAsRefund'].includes(data?.statusOms) &&
            !data?.newOrderExchangeId &&
            hasActionRefund(['bank']),
          'Mark as Refund',
          'markAsRefund',
        ),
      ].filter((option) => option !== null),
    [data],
  );

  const tempOrderStatus = SELLER_ORDER_STATUS_CONST[data?.statusOms];

  const handleParamsBreadcrumb = useMemo(() => {
    if (!KEY_PATH?.[data?.statusOms]) {
      return {
        title: 'All',
        href: '/order-management',
      };
    }
    if (data?.statusOms === 'PENDING_PROCESSING') {
      return {
        title: PENDING_TYPE[data?.pendingType]?.title,
        href: `/order-management?customLayout=PENDING&statusOms=${PENDING_TYPE[data?.pendingType]?.path}`,
      };
    }
    return {
      title: KEY_PATH?.[data?.statusOms]?.title,
      href: `/order-management?customLayout=${KEY_PATH?.[data?.statusOms]?.keyPath}&statusOms=${data?.statusOms}`,
    };
  }, [data?.statusOms]);

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'common.page.orders',
        href: '/order-management',
      },
      {
        title: handleParamsBreadcrumb?.title,
        href: handleParamsBreadcrumb?.href,
      },
      ['PICKING', 'PACKING'].includes(data?.statusOms)
        ? {
            title: `Batch ID ${formatDateTime(data?.createdAt, 'DDMMYYYY')}_${data?.orderBatch?.id}`,
            href: `/order-management/${data?.orderBatch?.id}/detail?status=${data?.statusOms}`,
          }
        : {},
      {
        title: (
          <span className="font-bold text-sm text-[--primary]">
            {`#${data?.orderNumber}`}
          </span>
        ),
      },
    ],
    [data?.orderNumber, data?.orderBatch?.id, data?.statusOms],
  );

  const cancelOrder = () => {
    setCancelVisible(true);
  };

  const openDeliveryModal = () => {
    dispatch(
      crudActions.modal.toogleModal({
        ['editDelivery']: {
          isOpen: true,
        },
      } as any),
    );
  };

  const onForceToCOD = () => {
    Modal.confirm({
      content: t('orders.confirmChangeToCODAlert'),
      onOk: () => {
        dispatch(confirmForceToCOD(data));
      },
    });
  };

  const handleModalMarkAsSuccess = (value) => {
    dispatch(
      crudActions.modal.toogleModal({
        ['markAsSuccess']: {
          isOpen: value,
        },
      } as any),
    );
  };

  const handleMarkAsSuccess = () => {
    setLoadingMarkAsSuccess(true);
    dispatch(markDeliverySuccess({ data: data?.id }))
      .then((e: DynamicObjectType) => {
        if (e?.payload?.success) {
          notification.success({
            message: 'Success',
            description: 'Mark order delivery success',
          });
          setLoadingMarkAsSuccess(false);
          handleModalMarkAsSuccess(false);
          window.location.reload();
        }
      })
      .then(() => setLoadingMarkAsSuccess(false));
  };

  const handleRevertToPreviousState = () => {
    Modal.confirm({
      content: t('orders.revertToPreviousState'),
      onOk: () => {
        dispatch(revertToPreviousState({ data: { id: data?.id } })).then(
          (res) => {
            if (res?.payload?.success) {
              window.location.reload();
            }
          },
        );
      },
    });
  };

  const handleMoveToReadyToPick = () => {
    Modal.confirm({
      content: t('orders.moveToReadyToPick'),
      onOk: () => {
        dispatch(moveToReadyToPick({ data: { id: data?.id } })).then((res) => {
          if (res?.payload?.success) {
            const searchParams = new URLSearchParams(search);
            searchParams.set(
              'filters',
              JSON.stringify(
                [
                  {
                    condition: 'and',
                    key: 'locationCode',
                    operator: 'contL',
                    value: 'onlhcm',
                  },
                ],
                null,
                0,
              ),
            );
            navigate(
              `/order-management?status=PROCESSING&statusOms=READY_TO_PICK&${searchParams.toString()}`,
            );
          }
        });
      },
    });
  };

  const handleMoveToLost = () => {
    Modal.confirm({
      content: t('orders.moveToLost'),
      onOk: () => {
        dispatch(moveToLost({ data: { id: data?.id } })).then(
          (res: DynamicObjectType) => {
            if (!res?.error?.message) {
              window.location.reload();
            }
          },
        );
      },
    });
  };

  const handlePrintLabel = () => {
    // setPrintType('printLabelBig');
    setVisiblePrintModal(true);
  };

  const handleExportGDN = () => {
    // setPrintType('printDeliveryBill');
    setVisiblePrintModal(true);
  };

  const openModalConfirmRefund = () => {
    confirmRefundModalRef.current.openModal();
  };
  const handleMarkAsRefundExchange = () => {
    // dispatch(markAsRefundExchange({ data: { id: data?.id } }));
    Modal.confirm({
      content: t('orders.markAsRefundExchange'),
      onOk: () => {
        dispatch(markAsRefundExchange({ data: { id: data?.id } }));
      },
    });
  };

  const handleExtraOptions = (e) => {
    switch (e.key) {
      case 'editDelivery': {
        return openDeliveryModal();
      }
      case 'refreshOrder': {
        return setRefreshVisible(true);
      }
      case 'editInvoice': {
        return setEditInvoiceVisible(true);
      }
      case 'confirmPaid': {
        return setConfirmPaidModal(true);
      }
      case 'changePaymentToCOD': {
        return onForceToCOD();
      }
      case 'replaceSKU': {
        return setCurrentVariantSKU(true);
      }
      case 'confirmOrder': {
        return setConfirmVisible(true);
      }
      case 'printPickingList': {
        return () => {};
      }
      case 'printLabel': {
        return handlePrintLabel();
      }
      case 'markAsSuccess': {
        return handleModalMarkAsSuccess(true);
      }
      case 'moveToLost': {
        return handleMoveToLost();
      }
      case 'exportGDN': {
        return handleExportGDN();
      }
      case 'moveToPending':
        return setMoveToPendingModal(true);
      case 'revertToPreviousState':
        return handleRevertToPreviousState();
      case 'moveToReadyToPick':
        return handleMoveToReadyToPick();
      case 'confirmRefund':
        return openModalConfirmRefund();
      case 'confirmRefundExchange':
        return openModalConfirmRefund();
      case 'markAsRefundExchange':
      case 'markAsRefund':
        return handleMarkAsRefundExchange();
      case 'cancelOrder':
        cancelOrder();
    }
  };
  return (
    <>
      <RestShow
        model="orders"
        breadcrumbs={breadcrumbs?.filter((item) => !!item?.title)}
        extraBreadcrumb={
          <Tag
            color={SELLER_ORDER_STATUS_CONST[data?.statusOms]?.background}
            style={{ borderRadius: 5, padding: '5px 10px', border: 'none' }}
          >
            <div
              style={{
                color: SELLER_ORDER_STATUS_CONST[data?.statusOms]?.color,
              }}
            >
              {t(tempOrderStatus?.text)}
            </div>
          </Tag>
        }
        extraOptions={EXTRA_OPTIONS}
        handleExtraOptions={handleExtraOptions}
        apiOptions={{
          extraParams: {
            join: JOIN,
          },
          isRequestApi: true,
        }}
      >
        <ShowContent data={data} />
      </RestShow>
      <RefreshOrderModal
        visible={refreshVisible}
        onCancel={() => setRefreshVisible(false)}
      />
      <CancelModal
        visible={cancelVisible}
        onCancel={() => setCancelVisible(false)}
        {...data}
      />
      <EditInvoiceModal
        visible={editInvoiceVisible}
        onCancel={() => setEditInvoiceVisible(false)}
      />
      <ReplaceModal
        oldVariantSku={currentVariantSKU}
        onCancel={() => setCurrentVariantSKU(null)}
        href={`${process.env.REACT_APP_IMS_URL}/products`}
        id={data?.id}
      />
      <ReplaceSKUModal
        visible={currentVariantSKU}
        onCancel={() => setCurrentVariantSKU(false)}
        href={`${process.env.REACT_APP_IMS_URL}/products`}
        id={data?.id}
        currentOrder={data}
      />
      <ConfirmModal
        visible={confirmVisible}
        data={data}
        onCancel={() => setConfirmVisible(false)}
      />
      <ConfirmPaidModal
        id={data?.id}
        visible={confirmPaidModal}
        onCancel={() => setConfirmPaidModal(false)}
      />
      <MoveToPendingModal
        id={data?.id}
        visible={moveToPendingModal}
        onCancel={() => setMoveToPendingModal(false)}
      />
      <PrintLabelActions
        orders={{
          [data?.id]: data,
        }}
        selectedRowKeys={[data?.id]}
        printActions={
          differentLabelChannels.includes(data?.channel)
            ? ['printLabelBig']
            : ['printLabelBig', 'printLabelSmall']
        }
        visible={visiblePrintModal}
        setVisible={setVisiblePrintModal}
        initPrintType={'printLabelBig'}
      />
      {/* <PrintLabelModal
        selectedRowKeys={[data?.id]}
        orders={{
          [data?.id]: data,
        }}
        visible={visiblePrintModal}
        setVisible={setVisiblePrintModal}
      /> */}
      <MarkAsSuccessModal
        onCancel={() => handleModalMarkAsSuccess(false)}
        handleSubmit={() => handleMarkAsSuccess()}
        loading={loadingMarkAsSuccess}
      />
      <ConfirmRefundModal ref={confirmRefundModalRef} data={data} />
    </>
  );
};

export default Show;
